export interface MediaAttemptStore {
  getString(key: string): string | undefined;
  set(key: string, value: string): void;
  delete(key: string): void;
  getAllKeys(): string[];
}

export interface MediaPreparation { localUri: string; expectedValue: string; retryAt?: number }

export interface MediaAttempt {
  id: string;
  owner: string;
  identity: string;
  checklistId: string;
  fieldId: string;
  source: 'camera' | 'gallery' | 'signature' | 'mark' | 'upload';
  at: number;
  status: 'started' | 'interrupted' | 'failed';
  phase?: string;
  errorCode?: string;
  preparation?: MediaPreparation;
  reported?: boolean;
}

const PREFIX = 'media_attempt_v1:';
const TTL = 7 * 24 * 60 * 60 * 1000;
const MAX_ATTEMPTS = 32;
const validId = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9_-]{1,80}$/.test(value);

/** Registro minimo imediato: nao depende do buffer de logs nem armazena conteudo da midia. */
export class MediaAttemptJournal {
  constructor(
    private readonly store: MediaAttemptStore,
    private readonly owner: string,
    private readonly now = Date.now,
  ) {}

  private read(key: string): MediaAttempt | null {
    try {
      const raw = this.store.getString(key);
      if (!raw || raw.length > 8192) return null;
      const value = JSON.parse(raw) as MediaAttempt;
      if (!validId(value.id) || !validId(value.owner) || !validId(value.checklistId) || !validId(value.fieldId) ||
          typeof value.identity !== 'string' || value.identity.split(':').length !== 2 ||
          !value.identity.split(':').every(validId) || !['camera', 'gallery', 'signature', 'mark', 'upload'].includes(value.source) ||
          !['started', 'interrupted', 'failed'].includes(value.status) || !Number.isFinite(value.at) ||
          value.at > this.now() || (value.at < this.now() - TTL && !value.preparation) || key !== PREFIX + value.id) return null;
      if (value.preparation && (typeof value.preparation.localUri !== 'string' ||
          !/^(?:file:\/\/.*\/)?checklist-(?:photos|signatures|marcacoes)\/[A-Za-z0-9._/-]+$/.test(value.preparation.localUri) ||
          value.preparation.localUri.length > 2048 || typeof value.preparation.expectedValue !== 'string' ||
          value.preparation.expectedValue.length > 2048)) return null;
      return { ...(value.preparation ? { preparation: value.preparation } : {}),
        ...(value.reported === true ? { reported: true } : {}), id: value.id, owner: value.owner, identity: value.identity, checklistId: value.checklistId,
        fieldId: value.fieldId, source: value.source, at: value.at, status: value.status,
        ...(typeof value.phase === 'string' && /^[a-z_]{1,40}$/.test(value.phase) ? { phase: value.phase } : {}),
        ...(['database_locked','storage_full','source_missing','network','local_transaction','other'].includes(value.errorCode ?? '') ? { errorCode: value.errorCode } : {}) };
    } catch { return null; }
  }

  private entries(): MediaAttempt[] {
    try {
      return this.store.getAllKeys().filter(key => key.startsWith(PREFIX))
        .map(key => {
          const value = this.read(key);
          if (!value) this.store.delete(key);
          return value;
        }).filter((value): value is MediaAttempt => value !== null);
    } catch { return []; }
  }

  start(input: Pick<MediaAttempt, 'id' | 'identity' | 'checklistId' | 'fieldId' | 'source'>): boolean {
    try {
      const value: MediaAttempt = { ...input, owner: this.owner, at: this.now(), status: 'started' };
      if (![value.id, value.owner, value.checklistId, value.fieldId].every(validId) ||
          value.identity.split(':').length !== 2 || !value.identity.split(':').every(validId) ||
          !['camera', 'gallery', 'signature', 'mark', 'upload'].includes(value.source)) return false;
      if (this.read(PREFIX + value.id)) return false;
      // Limite conservador: nao expulsar uma captura em andamento para abrir outra.
      const entries = this.entries();
      if (entries.length >= MAX_ATTEMPTS) return false;
      this.store.set(PREFIX + value.id, JSON.stringify(value));
      return true;
    } catch { return false; }
  }

  advance(id: string, identity: string, phase: string): void {
    try {
      const value = this.read(PREFIX + id);
      if (value?.identity === identity && value.owner === this.owner && value.status === 'started' && /^[a-z_]{1,40}$/.test(phase)) {
        this.store.set(PREFIX + id, JSON.stringify({ ...value, phase, at: this.now() }));
      }
    } catch { /* Apenas etapas importantes, sem depender do flush dos logs. */ }
  }

  finish(id: string, identity: string, failed = false, details?: { phase?: string; errorCode?: string }): boolean {
    try {
      const value = this.read(PREFIX + id);
      if (!value || value.identity !== identity || value.owner !== this.owner) return false;
      if (failed) this.store.set(PREFIX + id, JSON.stringify({ ...value, ...details, status: 'failed', at: this.now() }));
      else this.store.delete(PREFIX + id);
      return true;
    } catch { return false; }
  }

  retainPreparation(id: string, identity: string, preparation: MediaPreparation): boolean {
    try {
      const value = this.read(PREFIX + id);
      if (!value || value.identity !== identity || value.owner !== this.owner) return false;
      if (value.preparation) return value.preparation.localUri === preparation.localUri;
      this.store.set(PREFIX + id, JSON.stringify({ ...value, preparation }));
      if (!this.read(PREFIX + id)?.preparation) return false;
      // Uma nova captura do campo substitui a retomada anterior, sem excluir seus bytes.
      for (const other of this.entries()) {
        if (other.id !== id && other.identity === identity && other.checklistId === value.checklistId &&
            other.fieldId === value.fieldId && other.preparation) {
          const { preparation: _, ...retained } = other;
          this.store.set(PREFIX + other.id, JSON.stringify({ ...retained, phase: 'superseded' }));
        }
      }
      return true;
    } catch { return false; }
  }

  async claimPreparations(identity: string, isOwnerAlive: (owner: string) => Promise<boolean>): Promise<MediaAttempt[]> {
    const claimed: MediaAttempt[] = [];
    const entries = this.entries();
    for (const value of entries) {
      if (entries.some(other => other.id !== value.id && other.identity === identity && other.checklistId === value.checklistId &&
          other.fieldId === value.fieldId && other.status === 'started' && other.at >= value.at)) continue;
      if (value.identity !== identity || !value.preparation ||
          (value.owner === this.owner && value.status === 'started') ||
          this.now() - (value.preparation.retryAt ?? 0) < 60_000) continue;
      if (value.owner !== this.owner && await isOwnerAlive(value.owner)) continue;
      const current = this.read(PREFIX + value.id);
      if (!current?.preparation || current.preparation.localUri !== value.preparation.localUri) continue;
      const next = { ...current, owner: this.owner, preparation: { ...current.preparation, retryAt: this.now() } };
      this.store.set(PREFIX + value.id, JSON.stringify(next));
      claimed.push(next);
    }
    return claimed;
  }

  async recover(identity: string, isOwnerAlive: (owner: string) => Promise<boolean>): Promise<MediaAttempt[]> {
    for (const value of this.entries()) {
      if (value.identity !== identity || value.owner === this.owner || value.status !== 'started') continue;
      try {
        if (await isOwnerAlive(value.owner)) continue;
        // Relê apos a consulta: a outra execucao pode ter concluido ou removido a tentativa.
        const current = this.read(PREFIX + value.id);
        if (current?.owner === value.owner && current.status === 'started') {
          this.store.set(PREFIX + value.id, JSON.stringify({ ...current, status: 'interrupted' }));
        }
      } catch { /* Sem prova de encerramento, preservar sem declarar interrupcao. */ }
    }
    return this.pending(identity);
  }

  pending(identity: string): MediaAttempt[] {
    return this.entries().filter(value => value.identity === identity && value.status !== 'started' && !value.reported);
  }

  acknowledge(identity: string, ids: string[]): void {
    for (const id of ids) {
      try {
        const value = this.read(PREFIX + id);
        if (value?.identity === identity && value.status !== 'started') {
          if (value.preparation) this.store.set(PREFIX + id, JSON.stringify({ ...value, reported: true }));
          else this.store.delete(PREFIX + id);
        }
      } catch { /* Repeticao posterior continua idempotente pelo ticket de diagnostico. */ }
    }
  }
}
